import React, { useCallback, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { useSelector } from "react-redux";

import DashboardLayout from "../../components/DashboardLayout";
import OrbitalLoader from "../../components/common/OrbitalLoader";
import LegacyBrandRedirect from "../../components/brands/LegacyBrandRedirect";
import { brandsService } from "../../services/brands";
import Overview from "./Overview";
import Profile from "./Profile";
import BrandSettings from "./Settings";
import BrandNewRoutes from "./New";

const BRAND_TABS = [
  {
    id: "overview",
    label: "Overview",
    path: "overview",
    to: "/brand/overview/",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  {
    id: "profile",
    label: "Profile",
    path: "profile",
    to: "/brand/profile/",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z"
        />
      </svg>
    ),
  },
  {
    id: "settings",
    label: "Settings",
    path: "settings",
    to: "/brand/settings/",
    icon: (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
      </svg>
    ),
  },
];

const BrandLoader = () => (
  <div className="flex items-center justify-center min-h-[40vh]">
    <OrbitalLoader size={128} />
  </div>
);

const Brand = (authProps) => {
  const { currentUser } = authProps;
  const location = useLocation();
  const { currentProject, loaded: projectsLoaded } = useSelector(
    (state) => state.project,
  );
  const { currentBrand, brands, ready: brandsReady } = useSelector(
    (state) => state.brand,
  );
  const [brand, setBrand] = useState(null);
  const [loading, setLoading] = useState(true);
  const hash = currentBrand?.hash;

  const brandResults = brands;
  const isSyncingBrand = brandsReady && brandResults.length > 0 && !hash;
  const hasBrand = brandResults.length > 0 && !!hash;
  const needsNewBrand = brandsReady && brandResults.length === 0;
  const brandsLoading = !projectsLoaded || !brandsReady;

  const loadBrand = useCallback(() => {
    if (!hash) {
      setBrand(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    brandsService.getBrand(
      hash,
      (response) => {
        setBrand(response.data);
        setLoading(false);
      },
      () => {
        setBrand(null);
        setLoading(false);
      },
    );
  }, [hash]);

  useEffect(() => {
    loadBrand();
  }, [loadBrand]);

  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path.includes("/settings/")) return "settings";
    if (path.includes("/profile/")) return "profile";
    return "overview";
  };

  const activeTab = getActiveTabFromPath();
  const navConfig = BRAND_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.icon,
    to: tab.to,
    active: activeTab === tab.id,
  }));

  const primaryAction =
    currentProject?.hash && !brandsLoading && !isSyncingBrand ? (
      <Link
        to="/brand/new/"
        className="px-4 py-2 bg-theme-primary text-theme-button rounded-md hover:opacity-90"
      >
        New Brand
      </Link>
    ) : null;

  const brandProps = { brand, setBrand, hash, loadBrand };
  const layoutTitle = hasBrand ? brand?.name || "Brand" : "Brand";

  if (!currentProject?.hash) {
    return (
      <DashboardLayout title="Brand" currentUser={currentUser}>
        <div />
      </DashboardLayout>
    );
  }

  const brandTabRoutes = () => {
    if (loading) {
      return <Route path="*" element={<BrandLoader />} />;
    }
    if (!brand) {
      return (
        <Route
          path="*"
          element={
            <div className="text-theme-text opacity-80">Brand not found.</div>
          }
        />
      );
    }
    return (
      <>
        <Route path="overview/*" element={<Overview {...brandProps} />} />
        <Route path="profile/*" element={<Profile {...brandProps} />} />
        <Route path="settings/*" element={<BrandSettings {...brandProps} />} />
        <Route path=":legacyHash/*" element={<LegacyBrandRedirect />} />
        <Route path="*" element={<Navigate to="overview/" replace />} />
      </>
    );
  };

  return (
    <DashboardLayout
      title={layoutTitle}
      currentUser={currentUser}
      navConfig={navConfig}
      primaryAction={primaryAction}
    >
      {brandsLoading || isSyncingBrand ? (
        <BrandLoader />
      ) : (
        <Routes>
          <Route
            path="new/*"
            element={<BrandNewRoutes loadBrand={loadBrand} />}
          />
          <Route
            index
            element={
              hasBrand ? (
                <Navigate to="overview/" replace />
              ) : (
                <Navigate to="new/" replace />
              )
            }
          />
          {needsNewBrand ? (
            <>
              <Route
                path="overview/*"
                element={<Navigate to="/brand/new/" replace />}
              />
              <Route
                path="profile/*"
                element={<Navigate to="/brand/new/" replace />}
              />
              <Route
                path="settings/*"
                element={<Navigate to="/brand/new/" replace />}
              />
            </>
          ) : (
            brandTabRoutes()
          )}
        </Routes>
      )}
    </DashboardLayout>
  );
};

export default Brand;
